// Slice B2.4 of docs/planning/activity_dock_workflow_facilitation.md:
// thin React glue around the pure `evaluateDockPeek` helper. Lives next
// to `ActivityFloatingDock.tsx` so the dock stays under the file-size
// soft cap while gaining section-aware peek decisions.
//
// What it owns:
//   - reading the live `auth` + `urgency` contexts the section
//     predicates need;
//   - reading the role + bucket-filtered section list (same one the
//     panel renders, so visibility and peek-eligibility can't drift);
//   - memoizing the peek-context derivation so a parent re-render
//     doesn't recompute on every render when nothing relevant moved;
//   - returning the structured `{ shouldPeek, reasonSectionId }`
//     decision (Slice B2.4-B) so the peek hook can emit the section
//     that earned the peek as part of its telemetry event, not just
//     the fact that a peek happened.
//
// L2-followup of docs/planning/firefly_role_context_presentation.md:
// the bucket filter that L2 added to `useActivityDockSections` is now
// also wired into the peek path here. Before this fix, the peek
// decision read the role-only-filtered section list, which meant a
// section whose `bucketRelevance` excluded the current bucket could
// in principle drive a peek (caught today only because every peek-
// eligible section's `modeRelevance` already restricts it to modes
// within its declared bucket). Threading `currentBucket` closes the
// doctrinal gap so a future section with cross-bucket `modeRelevance`
// can't accidentally leak peeks into the wrong bucket.

import { useMemo } from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspaceUrgencyContext } from "@/contexts/WorkspaceUrgencyContext";
import { getBucketForMode } from "@/components/shell/modes/bucketsRegistry";
import { useActivityDockSections } from "./useActivityDockSections";
import {
  buildDockPeekContext,
  evaluateDockPeek,
  type DockPeekDecision,
} from "./dockPeekDecision";

/**
 * Returns the `DockPeekDecision` (`{ shouldPeek, reasonSectionId }`)
 * for the current dock state and `currentMode`. `shouldPeek` is `true`
 * iff at least one role-visible, mode-relevant section currently
 * reports a peek-worthy signal; `reasonSectionId` is the id of that
 * section (or `null` when no peek is warranted).
 *
 * Pure read; safe to call on every render. The peek hook
 * (`useActivityDockModePeek`) consumes the decision to decide whether
 * a mode transition is worth a transient auto-open AND to tag the
 * resulting telemetry event with the section that earned it.
 */
export function useActivityDockPeekDecision(
  currentMode: PrimaryMode | null | undefined,
  managerSignals?: {
    kbGapOpenCount?: number | null;
    aiPromptingOpenCount?: number | null;
  },
  adminSignals?: {
    pendingMemberApprovalTotal?: number | null;
  },
  mfgSignals?: {
    staleWacCount?: number | null;
    plantRequestReviewCount?: number | null;
    openQcCount?: number | null;
  },
): DockPeekDecision {
  const auth = useAuth();
  const urgency = useWorkspaceUrgencyContext();
  // L2-followup: derive `currentBucket` from `currentMode` the same
  // way `ActivityDockExpandedPanel` does, and pass it through so the
  // peek-eligible section list is bucket-filtered too. Mirrors the
  // panel's "one source of truth per render frame" pattern.
  const currentBucket = currentMode ? getBucketForMode(currentMode) : null;
  const sections = useActivityDockSections(currentBucket);

  const peekContext = useMemo(() => {
    return buildDockPeekContext({
      leads: urgency?.leads ?? [],
      adsActivity: urgency?.adsActivity ?? null,
      hasRole: (...roles: string[]) => auth.hasRole(...roles),
      authStatus: auth.status,
      kbGapOpenCount: managerSignals?.kbGapOpenCount ?? null,
      aiPromptingOpenCount: managerSignals?.aiPromptingOpenCount ?? null,
      pendingMemberApprovalTotal: adminSignals?.pendingMemberApprovalTotal ?? null,
      staleWacCount: mfgSignals?.staleWacCount ?? null,
      plantRequestReviewCount: mfgSignals?.plantRequestReviewCount ?? null,
      openQcCount: mfgSignals?.openQcCount ?? null,
    });
  }, [
    auth,
    urgency,
    managerSignals?.kbGapOpenCount,
    managerSignals?.aiPromptingOpenCount,
    adminSignals?.pendingMemberApprovalTotal,
    mfgSignals?.staleWacCount,
    mfgSignals?.plantRequestReviewCount,
    mfgSignals?.openQcCount,
  ]);

  return useMemo(
    () => evaluateDockPeek({ sections, currentMode, peekContext }),
    [sections, currentMode, peekContext],
  );
}
